import { CommaList } from "Components/CommaList";
import { Dividers } from "Components/Dividers";
import { useDocumentTitle } from "Components/Hooks";
import { Horizontal } from "Components/Horizontal";
import { Section } from "Components/Section";
import { useFormikContext } from "formik";
import { Program } from "SharedConstants";
import { FitnessInstructorManageCertificatesModel } from "SharedConstants/Schema";
import { appendQueryObject } from "Tools";
import { NamedComponent } from "Views/NamedComponents";
import {
  AddStandardAIInput,
  CalculateStandardAICost,
} from "Views/Shared/AddStandardAIInput";
import {
  BriefInformationRows,
  BriefPolicyInformation,
} from "Views/Shared/BriefPolicyInformation";
import { PaymentInput } from "Views/Shared/PaymentInput";
import { Heading } from "../../Components/Heading";
import { MVCForm, MVCFormServerProps, MVCSubmitButton } from "../MVCForm";
import { ModelStateValues } from "../Tools";

export function ManageCertificates(
  props: FitnessInstructorManageCertificatesModel & {
    formProps: MVCFormServerProps;
  },
) {
  const getInitialValues = (values: ModelStateValues) => {
    return {
      ...values,
    };
  };

  const isSup = props.RequestData.Program === Program.Sup;
  const title = "Manage Certificates";
  useDocumentTitle(`${title} - ${props.RequestData.ProducerName}`);
  return (
    <>
      {props.TopBarNotificationProps && (
        <NamedComponent
          {...props.TopBarNotificationProps}
          RequestData={props.RequestData}
        />
      )}
      <Heading title={title} />
      {props.IsPolicyExpired ? (
        <>
          <div
            className="has-text-weight-bold has-text-centered"
            style={{ marginBottom: "1em" }}
          >
            This policy has expired.
          </div>
          <div className="has-text-centered">
            <a className="button is-primary" href={"Highlights"}>
              Apply for a New Policy
            </a>
          </div>
        </>
      ) : (
        <MVCForm
          {...props.formProps}
          initialValues={getInitialValues(props.formProps.initialValues)}
        >
          {({ errors }) => (
            <Dividers>
              <>
                {errors["GeneralError"]}
                <BriefPolicyInformation
                  {...props.BriefPolicyInformationProps}
                />
                <BriefInformationRows
                  items={[
                    [
                      `${isSup ? "SUP Instructor" : "Personal Trainer"} Type${
                        props.FitnessInstructorTypes.length > 1 ? "s" : ""
                      }`,
                      <CommaList items={props.FitnessInstructorTypes} />,
                    ],
                  ]}
                />
              </>
              {props.RenewalUrl && (
                <Section title="Policy Renewal">
                  <div className="has-text-centered">
                    <a className="button is-primary" href={props.RenewalUrl}>
                      Renew My Policy
                    </a>
                  </div>
                </Section>
              )}
              {props.ManageRenewalPolicyUrl && (
                <Section title="Policy Renewal">
                  <div className="has-text-centered">
                    <a
                      className="button is-primary"
                      href={props.ManageRenewalPolicyUrl}
                      style={{ height: "auto", flexDirection: "column" }}
                    >
                      <div>Manage My Renewal Policy</div>
                      <div className="is-italic">
                        ({props.RenewalPolicyFirstDate} through{" "}
                        {props.RenewalPolicyExpirationDate})
                      </div>
                    </a>
                  </div>
                </Section>
              )}
              <ExistingCertificatesSection {...props} />
              <Section title="New Additional Insured">
                {props.CanAddAI ? (
                  <AddAIInputAndNotes {...props} isSup={isSup} />
                ) : (
                  <div className="is-italic">
                    The maximum number of Additional Insureds have been added.
                    To add more, please contact us using the information below.
                  </div>
                )}
              </Section>
            </Dividers>
          )}
        </MVCForm>
      )}
    </>
  );
}

function AddAIInputAndNotes(
  props: FitnessInstructorManageCertificatesModel & {
    isSup: boolean;
  },
) {
  const { values, isSubmitting } = useFormikContext<any>();
  const totalCost =
    props.BaseAICost +
    CalculateStandardAICost(props.AddStandardAIInputProps, values);
  return (
    <div className="narrowFieldBodies3">
      <Horizontal>
        <div dangerouslySetInnerHTML={props.Header} />
      </Horizontal>
      <AddStandardAIInput {...props.AddStandardAIInputProps} />
      {totalCost > 0 && (
        <PaymentInput {...props.PaymentInputProps} TotalCost={totalCost} />
      )}
      <div className="has-text-centered">
        <MVCSubmitButton
          id="SubmitButton"
          className="button"
          value="submit"
          disabled={isSubmitting}
          isPrimary
        >
          Submit this Additional Insured
        </MVCSubmitButton>
      </div>
    </div>
  );
}

function ExistingCertificatesSection(
  props: FitnessInstructorManageCertificatesModel,
) {
  return (
    <Section title="Existing Certificate(s)">
      <div className="has-text-centered">
        <div>Proof of Insurance</div>
        <div>
          <a
            className="button is-primary"
            href={props.DownloadCertificateUrlBase}
          >
            Download Certificate
          </a>
        </div>
      </div>
      {props.AdditionalInsureds.map(e => (
        <div
          key={e.StandardAdditionalInsuredID}
          className="has-text-centered"
          style={{ marginTop: "1em" }}
        >
          <div>{e.Name}</div>
          <div>
            <a
              className="button is-primary"
              href={
                appendQueryObject(props.DownloadCertificateUrlBase, {
                  CertificateID: e.StandardAdditionalInsuredID,
                }).href
              }
            >
              Download Certificate
            </a>
          </div>
        </div>
      ))}
    </Section>
  );
}
